const relativeUrlBase = "https://sentry.invalid"
const piwikContainerPath = "/5c636016-1d75-4d02-9726-8ca34329c302.js"
const piwikContainerUrl = `https://solaris.containers.piwik.pro${piwikContainerPath}`
const ignoredPiwikTemplateMessages = [
  `Cannot fetch templates: request blocked by the browser
Possible reasons:
- request blocking browser extension
- request URL is blocked in browser's developer tools`,
  "Cannot fetch templates: 204",
]

export function scrubBrowserEvent(event) {
  if (isIgnoredPiwikTemplateError(event)) {
    return null
  }

  // Remove context that can contain form values or other personal data.
  delete event.extra
  delete event.user

  const request = sanitizeBrowserRequest(event.request)

  if (request) {
    event.request = request
  } else {
    delete event.request
  }

  return event
}

export function scrubBrowserBreadcrumb(breadcrumb) {
  // Remove Sentry's own events and console messages that can contain personal data.
  if (
    breadcrumb.category === "console" ||
    breadcrumb.category?.startsWith("sentry.")
  ) {
    return null
  }

  const data = breadcrumb.data

  if (!data || typeof data !== "object") {
    return breadcrumb
  }

  const requestUrl = sanitizeSentryUrl(data.url)
  const statusCode = Number(data.status_code)
  const isNetworkRequest =
    breadcrumb.category === "fetch" || breadcrumb.category === "xhr"
  const isPageDataRequest =
    requestUrl &&
    new URL(requestUrl, relativeUrlBase).pathname.startsWith("/page-data/")
  const isSuccessful = statusCode >= 200 && statusCode < 400

  // Remove successful Gatsby preloads to reduce noise.
  if (isNetworkRequest && isPageDataRequest && isSuccessful) {
    return null
  }

  for (const key of ["url", "from", "to"]) {
    if (key in data) {
      const sanitizedUrl = sanitizeSentryUrl(data[key])

      if (sanitizedUrl) {
        data[key] = sanitizedUrl
      } else {
        delete data[key]
      }
    }
  }

  return breadcrumb
}

/*
  Piwik throws when its consent-template request fails or returns no content.
  Browser or network filtering can cause either variant. Suppress only these
  exact messages from our container to preserve quota and reduce noise.
 */
function isIgnoredPiwikTemplateError(event) {
  const exceptions = event.exception?.values

  if (exceptions?.length !== 1) {
    return false
  }

  const [exception] = exceptions
  if (
    exception.type !== "ApiCommandError" ||
    !ignoredPiwikTemplateMessages.includes(exception.value)
  ) {
    return false
  }

  // Sentry puts the originating frame last. Ignore query strings and fragments.
  const frames = exception.stacktrace?.frames || []
  const filename = frames[frames.length - 1]?.filename
  const script = filename?.split(/[?#]/)[0]

  return script === piwikContainerUrl || script === piwikContainerPath
}

function sanitizeBrowserRequest(request) {
  if (!request || typeof request !== "object") {
    return undefined
  }

  // Keep only diagnostic fields; omit cookies, bodies and other headers.
  const sanitized = {}
  const headers = {}
  const url = sanitizeSentryUrl(request.url)
  const userAgent = request.headers?.["User-Agent"]
  const referer = sanitizeSentryUrl(request.headers?.Referer)

  if (url) {
    sanitized.url = url
  }
  if (userAgent) {
    headers["User-Agent"] = userAgent
  }
  if (referer) {
    headers.Referer = referer
  }
  if (userAgent || referer) {
    sanitized.headers = headers
  }

  return url || userAgent || referer ? sanitized : undefined
}

function sanitizeSentryUrl(value) {
  if (typeof value !== "string" || value.trim() === "") {
    return undefined
  }

  try {
    const url = new URL(value, relativeUrlBase)

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return undefined
    }

    // Keep the origin and path, stripping credentials, query strings and fragments.
    return url.origin === relativeUrlBase
      ? url.pathname
      : `${url.origin}${url.pathname}`
  } catch {
    return undefined
  }
}
